import React, { useEffect, useState } from "react";
import { Provider } from "react-redux";
import { theme } from "./theme/theme";
import { ThemeProvider } from "styled-components";
import MainRoutes from "./routes";
import store from "./redux/store";
// eslint-disable-next-line
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import PropTypes from "prop-types";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastProvider } from "./context/ToastContext";
import { msalConfig } from '../src/auth/authConfig';
import { FeatureFlagProvider } from "./context/FeatureFlagContext";
import UpdateBanner from "./components/UpdateBanner";
import IdleTimeoutModal from "./components/IdleTimeoutModal";
import { startVersionCheck } from "./utils/versionCheck";
import { useIdleTimer } from "./hooks/useIdleTimer";

/**
 * This component is the entry point for APP.
 * It includes all providers and routes configured.
 * @returns {ReactNode} returns JSX element with all providers and routes.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // Data is considered fresh for 5 minutes
      cacheTime: 1000 * 60 * 10, // Cache remains active for 10 minutes
      retry: 2, // Retry failed queries 2 times
      refetchOnWindowFocus: false, // Disable refetching on window focus
    },
    mutations: {
      retry: 1, // Retry failed mutations once
    },
  },
});

/**
 * Inner App component with version checking and idle detection
 */
function AppWithFeatures({ msalInstance }) {
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const [newVersion, setNewVersion] = useState(null);
  const [releaseNote, setReleaseNote] = useState('');
  const [description, setDescription] = useState('');
  const [showIdleModal, setShowIdleModal] = useState(false);
  const [idleNotificationTimer, setIdleNotificationTimer] = useState(null);

  // Add a small grace period before showing the idle modal (10 seconds after idle is detected)
  const IDLE_GRACE_PERIOD = 10000; // 10 seconds
  const IDLE_TIMEOUT = 900000; // 15 minutes

  // Use idle timer hook
  const { isIdle, resetIdleTimer } = useIdleTimer(
    IDLE_TIMEOUT,
    () => {
      // Callback when idle is detected - show modal after grace period
      const timer = setTimeout(() => {
        setShowIdleModal(true);
      }, IDLE_GRACE_PERIOD);
      setIdleNotificationTimer(timer);
    }
  );

  // Set up version checking on component mount
  useEffect(() => {
    const versionCheckTimer = startVersionCheck(60000, (result) => {
      // 60 seconds interval
      if (result.updateAvailable) {
        setShowUpdateBanner(true);
        setNewVersion(result.newVersion);
        setReleaseNote(result.releaseNote || '');
        setDescription(result.description || '');
      }
    });

    // Cleanup version check timer on unmount
    return () => {
      if (versionCheckTimer) {
        clearInterval(versionCheckTimer);
      }
    };
  }, []);

  // Handle continue session button - reset the idle timer
  const handleContinueSession = () => {
    setShowIdleModal(false);
    resetIdleTimer();
    if (idleNotificationTimer) {
      clearTimeout(idleNotificationTimer);
    }
  };

  // Handle refresh button
  const handleRefresh = () => {
    window.location.reload();
  };

  // Handle close update banner
  const handleCloseUpdateBanner = () => {
    setShowUpdateBanner(false);
  };

  return (
    <>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            <Provider store={store}>
              <ToastProvider>
                <FeatureFlagProvider>
                  <MainRoutes />
                </FeatureFlagProvider>
              </ToastProvider>
            </Provider>
          </ThemeProvider>
        </QueryClientProvider>
      </MsalProvider>

      {/* Updat Banner - Shows when a new version is available */}
      <UpdateBanner
        isVisible={showUpdateBanner}
        newVersion={newVersion}
        releaseNote={releaseNote}
        description={description}
        onRefresh={handleRefresh}
        onClose={handleCloseUpdateBanner}
      />

      {/* Idle Timeout Modal - Shows when user has been inactive for 15 minutes */}
      <IdleTimeoutModal
        isVisible={showIdleModal}
        onContinue={handleContinueSession}
        onRefresh={handleRefresh}
      />
    </>
  );
}

function App({ msalInstance }) {
  return <AppWithFeatures msalInstance={msalInstance} />;
}

App.propTypes = {
  msalInstance: PropTypes.object.isRequired, // This line can be removed as msalInstance is initialized inside the component now
};

export default App;
